import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useChannelStateContext, useChatContext, useMessageContext } from "stream-chat-react";
import Avatar from "../common/Avatar";
import AnonSilhouetteAvatar from "../last-minute/AnonSilhouetteAvatar";
import { LockGlyph } from "../last-minute/lmGlyphs";
import FlagImg from "../agent-tender/FlagImg";
import { urgencyMeta, formatMarketValueExact, formatMarketValueRange } from "../../utils/lastMinute";
import { lastMinuteService } from "../../services/lastMinuteService";
import { ROLES } from "../../config/roles";

// In-chat Last Minute listing card (design 9d "Shared listing in chat", with the
// 2a/2b reveal grammar). Rendered from the `last_minute_listing` attachment the
// BE drops into a chat (club-contact + message-request accept flows; a share
// lands here too). The payload is the listing's anonymity-respecting context
// snapshot (Invariant 3 — an anonymized listing carries NO name/current club),
// so this card renders only what it is given.
//
// SIDES (design 9d): the card is action-asymmetric —
//   • sender side ("your side"): a single outlined-blue "Open listing";
//   • recipient side ("their side"): filled "Open listing" + outlined
//     "Add to radar". The radar action mirrors the BE gates exactly (club
//     viewer + NAMED listing only — dashboard/last_minute_views add_to_radar:
//     clubs only, anonymized → reveal first), so the button never renders where
//     the call would be rejected.
//
// OWNER REVEAL GRANT (design 2a): in the CLUB↔agent contact channel the BE stamps
// `lm_listing_id` + `lm_club_id` on the channel. The listing OWNER (the agent side)
// sees a "Reveal name to this club" action on an anonymized listing; granting posts
// the per-club reveal and swaps to the "Visible only to {club}" lock line (design
// 2b; Invariant 3 — club A being revealed never reveals to club B). Only the agent
// side sees it (the club never gets lm_club_id-gated owner controls); a named
// listing has nothing to reveal.
const clubNameFromChannel = (channel) => {
  const users = channel?.data?.users;
  if (!Array.isArray(users)) return "";
  const clubUser = users.find((u) => u.club_name) || users.find((u) => /club|scout|staff/i.test(u.role || ""));
  return clubUser?.club_name || clubUser?.name || "";
};

// Club roles gate the radar action (same list the Last Minute page uses).
const CLUB_ROLES = [ROLES.ClubAdmin, ROLES.Staff, ROLES.Coach];

const LastMinuteListingCard = (props) => {
  const navigate = useNavigate();
  const currentRole = useSelector((s) => s.auth.currentRole);
  const { channel } = useChannelStateContext() || {};
  const { message } = useMessageContext() || {};
  const { client } = useChatContext() || {};
  const [granted, setGranted] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [radarState, setRadarState] = useState("idle"); // idle | busy | done

  const a = props.attachments?.[0] || {};
  const meta = a.listing || a.metadata || {};
  const named = !meta.anonymized;
  // 9d: named card = name headline; anonymized = "{position}, {age}" headline
  // (never a name — the snapshot is structurally nameless anyway).
  const headline = named
    ? meta.name || meta.position || "Player"
    : [meta.position, meta.age].filter((v) => v !== null && v !== undefined && v !== "").join(", ");
  const urgency = urgencyMeta(meta.urgency_class);
  // Meta line per 9d ("CB · 24 · MV €4.5M · Portugal"); every part is optional —
  // today's snapshot carries position/age only, richer payloads fill in.
  const mv = named ? formatMarketValueExact(meta.market_value) : formatMarketValueRange(meta.market_value);
  const metaLine = named
    ? [meta.position, meta.age, mv, meta.country].filter((v) => v !== null && v !== undefined && v !== "").join(" · ")
    : ["Anonymized profile", mv].filter(Boolean).join(" · ");

  // Which side of the chat is this? The sharer/owner sees the sender variant.
  const isMine = !!message?.user?.id && !!client?.userID && message.user.id === client.userID;

  // Owner-side reveal gate. lm_club_id is present ONLY on a club↔agent contact
  // channel (not on agent↔agent chats), so this never fires in a colleague chat.
  const lmClubId = channel?.data?.lm_club_id;
  const lmListingId = meta.listing_id || channel?.data?.lm_listing_id;
  const isAgent = currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin;
  const canReveal = isAgent && !!meta.anonymized && !!lmClubId && !!lmListingId && !granted;
  const clubName = clubNameFromChannel(channel);

  // Recipient-side radar action (9d "their side" / 2b post-reveal): mirrors the
  // BE gates — clubs only, named listings only.
  const isClubViewer = CLUB_ROLES.includes(currentRole);
  const canRadar = !isMine && isClubViewer && named && !!lmListingId;

  const reveal = async () => {
    if (revealing) return;
    setRevealing(true);
    const res = await lastMinuteService.reveal(lmListingId, lmClubId);
    setRevealing(false);
    if (res.success) setGranted(true);
  };

  const addToRadar = async () => {
    if (radarState !== "idle") return;
    setRadarState("busy");
    const res = await lastMinuteService.addToRadar(lmListingId);
    setRadarState(res.success ? "done" : "idle");
  };

  const openListing = () => navigate(lmListingId ? `/last-minute?listing=${lmListingId}` : "/last-minute");

  return (
    <div className="w-[300px] max-w-full bg-white border border-[#E4E9F1] rounded-xl p-3 flex flex-col gap-[9px]">
      {/* Identity row: avatar + name/flag + meta line + urgency pill (9d) */}
      <div className="flex items-center gap-2.5">
        {named ? (
          <Avatar src={meta.image_url || null} alt={headline} fallbackText={headline} size={38} className="flex-none" />
        ) : (
          <AnonSilhouetteAvatar size={38} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-[5px]">
            <span className="text-[13px] font-bold text-[#14263D] truncate">{headline}</span>
            {named && <FlagImg country={meta.nationality} className="w-[14px] h-[10px] rounded-[2px]" />}
          </div>
          {metaLine && <p className="text-[11px] text-[#5A6B82] mt-px truncate">{metaLine}</p>}
        </div>
        <span
          className="inline-flex items-center gap-1 self-start px-2 py-[3px] rounded-full text-[10.5px] font-semibold whitespace-nowrap"
          style={{ backgroundColor: urgency.bg, color: urgency.text }}
        >
          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: urgency.dot }} />
          {urgency.label}
        </span>
      </div>

      {/* Provenance row: via-pill + listing agent (9d) */}
      <div className="flex items-center gap-1.5 text-[10.5px] text-[#8A98AC]">
        <span className="font-semibold text-[#71829A] bg-[#EDF1F6] rounded-full px-2 py-[2px] whitespace-nowrap">via Last Minute</span>
        {meta.agent && <span className="truncate">Listed by {meta.agent}</span>}
      </div>

      {/* Owner reveal grant (2a) / post-reveal lock line (2b) */}
      {granted ? (
        <p className="flex items-center gap-1.5 text-[11px] text-[#8A98AC]">
          <LockGlyph size="chat" />
          Visible only to {clubName || "this club"}
        </p>
      ) : canReveal ? (
        <button
          type="button"
          onClick={reveal}
          disabled={revealing}
          className="w-full flex items-center justify-center gap-1.5 rounded-[9px] border border-[#BDD9FF] bg-white text-[#1466D8] text-[12px] font-semibold py-[7px] hover:bg-[#F0F7FF] disabled:opacity-60"
        >
          <LockGlyph size="chat" color="#1466D8" />
          {revealing ? "Revealing…" : `Reveal name to ${clubName || "this club"}`}
        </button>
      ) : null}

      {/* Actions (9d): sender = outlined "Open listing"; recipient = filled
          "Open listing" + (club, named) "Add to radar" */}
      {isMine ? (
        <button
          type="button"
          onClick={openListing}
          className="w-full text-center bg-white border border-[#BDD9FF] text-[#1466D8] rounded-[9px] py-[7px] text-[12px] font-semibold hover:bg-[#F0F7FF]"
        >
          Open listing
        </button>
      ) : (
        <div className="flex gap-[7px]">
          <button
            type="button"
            onClick={openListing}
            className="flex-[1.2] text-center bg-[#2283FF] text-white rounded-[9px] py-[7px] text-[12px] font-semibold hover:bg-[#1B6FE0]"
          >
            Open listing
          </button>
          {canRadar && (
            <button
              type="button"
              onClick={addToRadar}
              disabled={radarState === "busy"}
              className={`flex-1 text-center rounded-[9px] py-[7px] text-[12px] font-semibold border ${
                radarState === "done"
                  ? "border-[#22A75D] bg-[#ECFDF5] text-[#0E7C3C]"
                  : "border-[#D8E0EA] bg-white text-[#33475E] hover:bg-gray-50 disabled:opacity-60"
              }`}
            >
              {radarState === "done" ? "On radar" : "Add to radar"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LastMinuteListingCard;
